<template>
  <div>
    <px-view-header title="Cadastro de compra" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>
    <div v-if="loadingCompra">
      <px-circular-loading/>
    </div>
    <v-stepper v-if="!loadingCompra"
               class="elevation-1 rounded-card"
               v-model="currentStep"
               vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">
        Condições e frete
      </v-stepper-step>
      <v-stepper-content step="1">
        <px-cadastro-compra-dados-basicos :compra="compra"
                                          :is-editing="isEdicao"
                                          :cadastros-adicionais="cadastrosAdicionais"
                                          :erp-campos-integracao="erpCamposIntegracao"
                                          :campos-obrigatorios="camposObrigatorios"
                                          @change-fornecedor="getDetalhesFornecedor"
                                          @cancel="onClickCancel"
                                          @ready="next"/>
      </v-stepper-content>
      <v-stepper-step :complete="currentStep > 2" step="2">Notificações</v-stepper-step>
      <v-stepper-content step="2">
        <px-notificar-fornecedor :compra="compra"
                                 :is-edicao="isEdicao"
                                 @remove="onRemoveMessage"
                                 @ready="next"
                                 @previous="previous"/>
      </v-stepper-content>
      <v-stepper-step :complete="currentStep > 3" step="3">Produtos</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-compra-produtos :compra="compra"
                                     :erp-campos-integracao="erpCamposIntegracao"
                                     :campos-obrigatorios="camposObrigatorios"
                                     :is-editing="isEdicao"
                                     :is-rascunho="isRascunho"
                                     :usa-tabela-preco="true"
                                     :loading="loading"
                                     @ready="onClickSalvar"
                                     @previous="previous"
                                     @save-draft="onSaveDraft"/>
      </v-stepper-content>
    </v-stepper>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>

<script>
import {
  FinalidadeCompra, Moeda, OrigemCompra, SituacaoCompra, TipoFrete,
} from 'px-business-components';
import CompradorService from '@/api/comprador-service';
import { mapGetters } from 'vuex';
import moment from 'moment';
import {
  checkCadastrosAdicionaisErp,
  computeFrom,
  initialStateCamposErp,
} from '@/modules/carrinho/finalizacao/campos-erp/validate-campos-erp';
import GestaoFornecedorService from '@/modules/gestao-fornecedor/gestao-fornecedor-service';
import { getCadastroAdicional } from '@/modules/cadastros-adicionais/cadastros-adicionais-utils';
import {
  checkCadastrosAdicionaisProdutoErp,
} from '@/modules/carrinho/finalizacao/item-campos-erp/validate-campos-erp';
import { parseBooleanQueryParam } from '@/utils/query-param-utils';
import { getTipoAplicacaoIcms, getTipoAplicacaoIpi } from '@/utils/imposto-utils';
import { formatToCode } from '@/utils/moment-date-utils';
import { initalStateProduto } from '@/modules/compra/cadastro/compra-utils';
import { uniqueId } from 'lodash';
import { calcularSaving } from '@/modules/compra/cadastro/calculo-compra.service';
import EntidadeCamposCompraService from '@/modules/erp-campos-obrigatorios/entidade-campos-compra-service';
import CadastroCompraDadosBasicos from './CadastroCompraDadosBasicos.vue';
import CadastroCompraProdutos from './CadastroCompraProdutos.vue';
import CadastroNotificarFornecedor from './CadastroNotificarFornecedor.vue';
import CompraService from '../compra-service';
import TipoNotaEntradaService from '../../tipo-nota-entrada/tipo-nota-entrada-service';

export default {
  data() {
    return {
      currentStep: 1,
      loading: false,
      loadingCompra: false,
      cadastrosAdicionais: null,
      camposObrigatorios: null,
      formDirt: true,
      compra: {
        comprador: null,
        compradorEntrega: null,
        moeda: Moeda.keys.BRL.key,
        dataCompra: formatToCode(moment()),
        fornecedor: null,
        centroCusto: null,
        projeto: null,
        transportadora: null,
        formaPagamento: null,
        prazoPagamento: null,
        dataFaturamento: formatToCode(moment()),
        observacao: null,
        situacao: null,
        mensagem: null,
        saving: 0,
        notificaFornecedor: false,
        finalidadeCompra: FinalidadeCompra.keys.USO_CONSUMO.key,
        origemCompra: OrigemCompra.keys.CADASTRO.key,
        modoPagamento: null,
        frete: {
          tipoFrete: null,
          prazoEntrega: null,
          totalTransporte: null,
        },
        arquivos: [],
        produtos: [],
        mensagensPersonalizadas: [],
        dataNecessidadeEntregaSolicitacao: null,
        tipoNotaEntrada: null,
      },
      isRascunho: parseBooleanQueryParam(this.$route.query.isRascunho),
      isCopy: parseBooleanQueryParam(this.$route.query.isCopy),
      idCompra: this.$route.params.id,
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Compras',
          to: '/compras',
        },
        { text: 'Cadastro de compras' },
      ],
    };
  },
  computed: {
    ...mapGetters('user', ['erpCamposIntegracao']),
    confirmMessage() {
      return this.isRascunho ? 'Você deseja atualizar o rascunho?' : 'Você deseja salvar a compra como rascunho?';
    },
    isEdicao() {
      return this.idCompra && !this.isRascunho && !this.isCopy;
    },
    isCif() {
      return compra => compra.frete.tipoFrete === TipoFrete.keys.CIF.key;
    },
    checkCadastrosAdicionais() {
      return !this.cadastrosAdicionais
        && checkCadastrosAdicionaisErp(this.erpCamposIntegracao, this.cadastrosAdicionais);
    },
    checkCadastroAdicionaisProduto() {
      return produto => !produto.cadastrosAdicionais
        && !checkCadastrosAdicionaisProdutoErp(this.erpCamposIntegracao,
          produto.cadastrosAdicionais);
    },
  },
  methods: {
    onRemoveMessage(item) {
      this.compra.mensagensPersonalizadas = this.compra
        .mensagensPersonalizadas.filter(mensagem => mensagem.id !== item.id);
    },
    onSaveDraft() {
      this.saveDraft();
    },
    onClickBack() {
      if (this.isEdicao) {
        this.$router.push({ name: 'main.compras' });
        return;
      }
      this.$refs.confirm.open(this.confirmMessage,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS na listagem de compras.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$router.push({ name: 'main.compras' });
            return;
          }
          this.saveDraft();
        });
    },
    onClickCancel() {
      this.$router.push({ name: 'main.compras' });
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    buildCadastrosAdicionaisPayload() {
      if (this.checkCadastrosAdicionais) {
        return [];
      }
      return [
        this.cadastrosAdicionais.natureza,
        this.cadastrosAdicionais.condicaoPagamento,
        this.cadastrosAdicionais.tipoCobranca,
      ].map((cadastro) => {
        if (cadastro) {
          return {
            id: cadastro.id,
          };
        }
        return null;
      }).filter(cadastro => cadastro);
    },
    getCadastroAdicionalProduto(produto, tipo) {
      if (!produto.cadastrosAdicionais) {
        return {};
      }
      return getCadastroAdicional(produto.cadastrosAdicionais, tipo);
    },
    buildCadastrosAdicionaisProdutoPayload(produto) {
      if (this.checkCadastroAdicionaisProduto(produto)) {
        return [];
      }
      return [
        produto.cadastrosAdicionais.armazem || this.getCadastroAdicionalProduto(produto.produto, 'ARMAZEM'),
        produto.cadastrosAdicionais.contaContabil || this.getCadastroAdicionalProduto(produto.produto, 'CONTA_CONTABIL'),
        produto.cadastrosAdicionais.contaFinanceira || this.getCadastroAdicionalProduto(produto.produto, 'CONTA_FINANCEIRA'),
      ].map((cadastro) => {
        if (cadastro) {
          return {
            id: cadastro.id,
          };
        }
        return null;
      }).filter(cadastro => cadastro);
    },
    buildCompra() {
      const compra = { ...this.compra };

      compra.tipoNotaEntrada = compra.tipoNotaEntrada || null;
      compra.dataNecessidadeEntregaSolicitacao = compra.dataNecessidadeEntregaSolicitacao || null;
      compra.frete.totalTransporte = compra.frete.totalTransporte || 0;
      compra.saving = calcularSaving(compra);
      compra.produtos = compra.produtos.map(produto => ({
        ...produto,
        impostos: {
          ...produto.impostos,
          tipoAplicacaoIcms: getTipoAplicacaoIcms(produto.impostos.icms),
          tipoAplicacaoIpi: getTipoAplicacaoIpi(produto.impostos.ipi),
        },
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        tipoNotaEntrada: compra.tipoNotaEntrada || null,
      }));
      return {
        ...compra,
        mensagem: compra.mensagem,
        cadastrosAdicionais: this.buildCadastrosAdicionaisPayload(),
      };
    },
    buildDraftCompra() {
      const compra = { ...this.compra };
      compra.tipoNotaEntrada = compra.tipoNotaEntrada || null;
      compra.produtos = compra.produtos.filter(produto => !!produto.produto);
      compra.produtos = compra.produtos.map(produto => ({
        ...produto,
        impostos: {
          ...produto.impostos,
          tipoAplicacaoIcms: getTipoAplicacaoIcms(produto.impostos.icms),
          tipoAplicacaoIpi: getTipoAplicacaoIpi(produto.impostos.ipi),
        },
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        tipoNotaEntrada: compra.tipoNotaEntrada,
      }));
      return {
        ...compra,
        mensagem: compra.mensagem,
        cadastrosAdicionais: this.buildCadastrosAdicionaisPayload(),
      };
    },
    onClickSalvar() {
      this.loading = true;
      CompraService.save(this.buildCompra(), this.compra.notificaFornecedor, this.isRascunho)
        .then(({ data }) => {
          let message = 'Compra criada com sucesso';
          if (this.idCompra && !this.isCopy) {
            message = 'Compra atualizada com sucesso';
          }
          this.$toast(message, { color: 'success' });

          if (data.tipo === 'APROVACAO') {
            this.$router.push({
              name: 'main.aprovacao.detalhes',
              params: {
                id: data.id,
              },
            });
            return;
          }

          this.$router.push({
            name: 'main.compras.detalhes',
            params: { id: data.id },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveDraft() {
      this.loading = true;
      return CompraService.saveRascunho(this.buildDraftCompra())
        .then(() => {
          let message = 'Rascunho salvo com sucesso';
          if (this.idCompra && this.isRascunho) {
            message = 'Rascunho atualizado com sucesso';
          }
          this.$toast(message, { color: 'success' });
          this.$router.push({ name: 'main.compras' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setupCopia(copiaData) {
      const compra = { ...copiaData };
      compra.id = undefined;
      compra.situacao = undefined;
      compra.dataCompra = formatToCode(moment());
      compra.mensagem = null;
      compra.codigoInterno = null;
      compra.mensagemFornecedor = null;
      compra.arquivos = [];
      compra.dataPrevisaoEntrega = null;
      compra.dataNecessidadeEntregaSolicitacao = null;
      if (compra.finalidadeCompra == null) {
        compra.finalidadeCompra = FinalidadeCompra.keys.USO_CONSUMO.key;
      }
      compra.origemCompra = OrigemCompra.keys.CADASTRO.key;
      compra.produtos = compra.produtos.map(prod => ({
        ...prod,
        $$id: uniqueId(),
        id: undefined,
        especificacaoProduto: this.getEspecificacao(prod),
        especificacaoProdutoOrigem: undefined,
        requisicaoProduto: undefined,
        respostaCotacaoProduto: undefined,
      }));
      compra.fornecedor = this.buildFornecedor(compra);
      compra.transportadora = this.getTransportadora(compra);

      this.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
        copiaData.cadastrosAdicionais);

      this.compra = compra;
    },
    buildFornecedor(compra) {
      if (compra.codigoInternoFornecedor) {
        return {
          ...compra.fornecedor,
          codigoInterno: compra.codigoInternoFornecedor,
        };
      }
      return {
        ...compra.fornecedor,
      };
    },
    getTransportadora(compra) {
      if (this.isCif(compra)) {
        return compra.fornecedor;
      }
      return this.buildTransportador(compra);
    },
    buildTransportador(compra) {
      if (compra.codigoInternoTransportadora) {
        return {
          ...compra.transportadora,
          codigoInterno: compra.codigoInternoTransportadora,
        };
      }
      return compra.transportadora;
    },
    getEspecificacao(produto) {
      if (produto.especificacaoProduto) {
        return produto.especificacaoProduto;
      }
      if (produto.especificacaoProdutoOrigem) {
        return produto.especificacaoProdutoOrigem;
      }
      return produto.produto ? produto.produto.especificacao : '';
    },
    setupRascunho(rascunhoData) {
      const compra = { ...rascunhoData };
      compra.situacao = SituacaoCompra.keys.RASCUNHO.key;
      compra.fornecedor = this.buildFornecedor(compra);
      compra.transportadora = this.getTransportadora(compra);

      this.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
        rascunhoData.cadastrosAdicionais);

      if (compra.produtos) {
        compra.produtos = compra.produtos.map(prod => ({
          ...prod,
          $$id: uniqueId(),
        }));
      }

      if (!compra.produtos || !compra.produtos.length) {
        compra.produtos = [initalStateProduto()];
      }

      this.compra = compra;
    },
    setupEdicao(compraAtual) {
      const compra = { ...compraAtual };
      compra.fornecedor = this.buildFornecedor(compra);
      compra.transportadora = this.getTransportadora(compra);

      if (compra.finalidadeCompra == null) {
        compra.finalidadeCompra = FinalidadeCompra.keys.USO_CONSUMO.key;
      }

      this.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
        compra.cadastrosAdicionais);
      this.compra = compra;
    },
    setup(data) {
      if (this.isRascunho) {
        this.setupRascunho(data);
        return;
      }
      if (this.isCopy) {
        this.setupCopia(data);
        return;
      }
      this.setupEdicao(data);
    },
    getCompra() {
      this.loadingCompra = true;
      CompraService.getCompraCompleto(this.idCompra)
        .then((response) => {
          this.setup(response.data);
        })
        .finally(() => {
          this.loadingCompra = false;
        });
    },
    beforeWindowUnload(e) {
      if (this.formDirt) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    getDetalhesFornecedor(id) {
      return GestaoFornecedorService.getByFornecedor(id)
        .then(({ data }) => {
          // fazer função auxiliar quando tiver mais opções de campos adicionais
          this.cadastrosAdicionais.natureza = getCadastroAdicional(data.cadastrosAdicionais,
            'NATUREZA');
        });
    },
    getCompradorDefaultFaturamento() {
      return CompradorService.getDefaultFaturamento()
        .then((response) => {
          this.compra.comprador = response.data;
        });
    },
    getCompradorDefaultEntrega() {
      return CompradorService.getDefaultEntrega()
        .then((response) => {
          this.compra.compradorEntrega = response.data;
        });
    },
    getNotaEntradaDefault() {
      if (this.compra.tipoNotaEntrada !== null) {
        return Promise.resolve();
      }
      return TipoNotaEntradaService.getNotaEntradaDefault()
        .then((response) => {
          this.compra.tipoNotaEntrada = response.data || null;
        });
    },
    getCamposObrigatorios() {
      EntidadeCamposCompraService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
  },
  created() {
    this.cadastrosAdicionais = initialStateCamposErp(this.erpCamposIntegracao);
    if (this.idCompra) {
      this.getCompra();
    }

    if (!this.compra.produtos.length) {
      this.compra.produtos.push(initalStateProduto());
    }
    window.addEventListener('beforeunload', this.beforeWindowUnload);

    this.getCamposObrigatorios();
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
  mounted() {
    if (this.idCompra) {
      return;
    }
    this.loadingCompra = true;

    Promise.all([
      this.getCompradorDefaultEntrega(),
      this.getCompradorDefaultFaturamento(),
      this.getNotaEntradaDefault(),
    ])
      .then(() => {
        this.loadingCompra = false;
      });
  },
  components: {
    pxCadastroCompraDadosBasicos: CadastroCompraDadosBasicos,
    pxCadastroCompraProdutos: CadastroCompraProdutos,
    pxNotificarFornecedor: CadastroNotificarFornecedor,
  },
};
</script>
<style scoped>
.button-div {
  display: flex;
  margin-bottom: 5px;
}
</style>
